import type { CardOptions } from '@atlaskit/editor-common/card';
import { EditorPresetBuilder } from '@atlaskit/editor-common/preset';
import type { ExtractPublicEditorAPI } from '@atlaskit/editor-common/types';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { analyticsPlugin } from '@atlaskit/editor-plugins/analytics';
import { basePlugin } from '@atlaskit/editor-plugins/base';
import { betterTypeHistoryPlugin } from '@atlaskit/editor-plugins/better-type-history';
import { blockTypePlugin } from '@atlaskit/editor-plugins/block-type';
import { cardPlugin } from '@atlaskit/editor-plugins/card';
import { clearMarksOnEmptyDocPlugin } from '@atlaskit/editor-plugins/clear-marks-on-empty-doc';
import { clipboardPlugin } from '@atlaskit/editor-plugins/clipboard';
import { codeBidiWarningPlugin } from '@atlaskit/editor-plugins/code-bidi-warning';
import { compositionPlugin } from '@atlaskit/editor-plugins/composition';
import { copyButtonPlugin } from '@atlaskit/editor-plugins/copy-button';
import { decorationsPlugin } from '@atlaskit/editor-plugins/decorations';
import { editorDisabledPlugin } from '@atlaskit/editor-plugins/editor-disabled';
import { emojiPlugin } from '@atlaskit/editor-plugins/emoji';
import { floatingToolbarPlugin } from '@atlaskit/editor-plugins/floating-toolbar';
import { focusPlugin } from '@atlaskit/editor-plugins/focus';
import { gridPlugin } from '@atlaskit/editor-plugins/grid';
import { highlightPlugin } from '@atlaskit/editor-plugins/highlight';
import { hyperlinkPlugin } from '@atlaskit/editor-plugins/hyperlink';
import { insertBlockPlugin } from '@atlaskit/editor-plugins/insert-block';
import { listPlugin } from '@atlaskit/editor-plugins/list';
import { mentionsPlugin } from '@atlaskit/editor-plugins/mentions';
import { pastePlugin } from '@atlaskit/editor-plugins/paste';
import { placeholderPlugin } from '@atlaskit/editor-plugins/placeholder';
import { quickInsertPlugin } from '@atlaskit/editor-plugins/quick-insert';
import { selectionPlugin } from '@atlaskit/editor-plugins/selection';
import { textColorPlugin } from '@atlaskit/editor-plugins/text-color';
import { textFormattingPlugin } from '@atlaskit/editor-plugins/text-formatting';
import { toolbarListsIndentationPlugin } from '@atlaskit/editor-plugins/toolbar-lists-indentation';
import { typeAheadPlugin } from '@atlaskit/editor-plugins/type-ahead';
import { unsupportedContentPlugin } from '@atlaskit/editor-plugins/unsupported-content';
import { widthPlugin } from '@atlaskit/editor-plugins/width';

const cardOptions: CardOptions = {
  allowBlockCards: false,
  allowEmbeds: false,
};

interface DatabasesPresetOptions {
  placeholderText?: string;
}

const createPreset = ({ placeholderText }: DatabasesPresetOptions) => {
  return new EditorPresetBuilder()
    .add([analyticsPlugin, {}])
    .add(focusPlugin)
    .add(compositionPlugin)
    .add(editorDisabledPlugin)
    .add(basePlugin)
    .add(textFormattingPlugin)
    .add(textColorPlugin)
    .add(highlightPlugin)
    .add(hyperlinkPlugin)
    .add(typeAheadPlugin)
    .add(selectionPlugin)
    .add(listPlugin)
    .add(betterTypeHistoryPlugin)
    .add(quickInsertPlugin)
    .add([insertBlockPlugin, { showElementBrowserLink: false }])
    .add(emojiPlugin)
    .add(widthPlugin)
    .add(decorationsPlugin)
    .add([
      blockTypePlugin,
      {
        allowBlockType: {
          exclude: ['heading', 'blockquote'],
        },
      },
    ])
    .add([placeholderPlugin, { placeholder: placeholderText }])
    .add(unsupportedContentPlugin)
    .add(clipboardPlugin)
    .add(codeBidiWarningPlugin)
    .add(clearMarksOnEmptyDocPlugin)
    .add(copyButtonPlugin)
    .add(mentionsPlugin)
    .add(floatingToolbarPlugin)
    .add([
      toolbarListsIndentationPlugin,
      { showIndentationButtons: false, allowHeadingAndParagraphIndentation: false },
    ])
    .add(gridPlugin)
    .add([cardPlugin, { ...cardOptions }])
    .add([pastePlugin, { cardOptions }]);
};

export type DatabasesEditorPreset = ReturnType<typeof createPreset>;
export type DatabasesEditorAPI =
  | ExtractPublicEditorAPI<DatabasesEditorPreset>
  | undefined;

export const useDatabasesPreset = (
  options: DatabasesPresetOptions
): DatabasesEditorPreset => {
  const { preset } = usePreset(() => createPreset(options), [options.placeholderText]);
  return preset;
};

export const useDatabasesPresetWithApi = (
  options: DatabasesPresetOptions
): { preset: DatabasesEditorPreset; editorApi: DatabasesEditorAPI } => {
  const { preset, editorApi } = usePreset(
    () => createPreset(options),
    [options.placeholderText]
  );
  return { preset, editorApi };
};
